// state/profile.ts — Local cache of the server-authoritative student profile.
//
// Single source of truth for the locally-stored student profile. Extracted from
// App.tsx in P6 STEP 3 so both App.tsx (onboarding write) and ProfileScreen
// (identity card + editable Data esame) read/write the same shape without a
// circular import (App imports ProfileScreen, so ProfileScreen must NOT import
// back from App).
//
// /api/me is the profile source of truth. These read and legacy edit helpers
// remain for current consumers until R-06 moves profile editing server-side.

import type {
  PatchStudentMeRequest,
  PatchStudentMeResult,
  StudentMe,
} from '../api/student-profile';
import {
  calendarDayDifference,
  europeRomeDateKey,
  extractExamDateKey,
  toExamDatePatchValue,
} from '../utils/exam-date';

export const PROFILE_LS_KEY = 'asc_profile_v1';
const CANONICAL_UTC_MILLISECOND_PATTERN = /^(\d{4})-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

export interface StoredProfile {
  firstName?: string;
  /** ISO date string, e.g. "2026-09-01" (date input) or full ISO from older writes. */
  examDate?: string;
  /** Driving-school name hydrated from /api/me when available. */
  school?: string;
  /** Server profile version retained for the R-06 write contract. */
  updatedAt?: string;
}

export interface StudentProfileEditDraft {
  firstName: string;
  examDateInput: string;
}

export type PrepareStudentProfilePatchResult =
  | { status: 'ready'; request: PatchStudentMeRequest }
  | { status: 'unchanged' }
  | { status: 'invalid'; field: 'firstName' | 'examDate' }
  | { status: 'unavailable'; reason: 'missing_profile_version' };

/** Read the whole stored profile object (empty object if absent/corrupt). */
export function getStoredProfile(): StoredProfile {
  try {
    const raw = localStorage.getItem(PROFILE_LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as StoredProfile;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function getStoredFirstName(): string | undefined {
  return getStoredProfile().firstName || undefined;
}

export function getStoredExamDate(): string | undefined {
  return getStoredProfile().examDate || undefined;
}

/** Overwrite the whole profile (used by onboarding). */
export function storeProfile(data: StoredProfile): void {
  try {
    localStorage.setItem(PROFILE_LS_KEY, JSON.stringify(data));
  } catch {
    /* quota or disabled storage — non-fatal */
  }
}

export type ServerProfileReplacementResult =
  | { status: 'success' }
  | { status: 'failure' };

/** Replace the local cache from server truth, with a verified all-or-fail write. */
export function replaceStoredProfileFromServer(me: StudentMe): ServerProfileReplacementResult {
  const next: StoredProfile = {
    firstName: me.firstName,
    updatedAt: me.updatedAt,
  };
  if (me.examDate !== null) next.examDate = me.examDate;
  if (me.schoolName !== null) next.school = me.schoolName;

  const serialized = JSON.stringify(next);
  try {
    localStorage.setItem(PROFILE_LS_KEY, serialized);
    return localStorage.getItem(PROFILE_LS_KEY) === serialized
      ? { status: 'success' }
      : { status: 'failure' };
  } catch {
    return { status: 'failure' };
  }
}

function isCanonicalUtcMillisecondTimestamp(value: string | undefined): value is string {
  if (value === undefined) return false;
  const match = CANONICAL_UTC_MILLISECOND_PATTERN.exec(value);
  if (!match || Number(match[1]) < 1) return false;
  const parsed = new Date(value);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString() === value;
}

export function prepareStudentProfilePatch(
  current: StoredProfile,
  draft: StudentProfileEditDraft,
): PrepareStudentProfilePatchResult {
  const firstName = draft.firstName.trim();
  if (firstName.length < 1 || firstName.length > 100) {
    return { status: 'invalid', field: 'firstName' };
  }

  const examDate = toExamDatePatchValue(draft.examDateInput);
  if (examDate === undefined) {
    return { status: 'invalid', field: 'examDate' };
  }

  if (!isCanonicalUtcMillisecondTimestamp(current.updatedAt)) {
    return { status: 'unavailable', reason: 'missing_profile_version' };
  }

  const request: PatchStudentMeRequest = { expectedUpdatedAt: current.updatedAt };
  if (firstName !== current.firstName) request.firstName = firstName;

  if (examDate === null) {
    if (current.examDate !== undefined) request.examDate = null;
  } else if (examDate !== extractExamDateKey(current.examDate)) {
    request.examDate = examDate;
  }

  if (request.firstName === undefined && request.examDate === undefined) {
    return { status: 'unchanged' };
  }
  return { status: 'ready', request };
}

export function replaceStoredProfileFromPatchResult(
  result: PatchStudentMeResult,
): ServerProfileReplacementResult {
  return replaceStoredProfileFromServer(
    result.status === 'updated' ? result.profile : result.current,
  );
}

/**
 * Merge a partial update into the stored profile (used by ProfileScreen to edit
 * Data esame without clobbering the name). Passing examDate: undefined clears it.
 */
export function updateStoredProfile(patch: Partial<StoredProfile>): StoredProfile {
  const next: StoredProfile = { ...getStoredProfile(), ...patch };
  // Drop empty-string / undefined keys so we don't persist "" as a value.
  if (!next.firstName) delete next.firstName;
  if (!next.examDate) delete next.examDate;
  storeProfile(next);
  return next;
}

/**
 * Human exam countdown for the identity card. Pure: `now` is injectable for
 * deterministic tests. Returns null when no (valid) exam date is set.
 *   - past   → "Esame passato"
 *   - today  → "Esame oggi"
 *   - +1     → "Esame domani"
 *   - +N     → "Esame tra N giorni"
 */
export function examCountdownLabel(iso: string | undefined, now: Date = new Date()): string | null {
  const examDateKey = extractExamDateKey(iso);
  const todayDateKey = europeRomeDateKey(now);
  if (!examDateKey || !todayDateKey) return null;
  const days = calendarDayDifference(todayDateKey, examDateKey);
  if (days === undefined) return null;
  if (days < 0) return 'Esame passato';
  if (days === 0) return 'Esame oggi';
  if (days === 1) return 'Esame domani';
  return `Esame tra ${days} giorni`;
}

/**
 * Normalise a yyyy-mm-dd value from a <input type="date"> into the date-only ISO
 * we persist locally. Returns undefined for empty input.
 */
export function normalizeExamDateInput(value: string): string | undefined {
  const v = value.trim();
  return v ? v : undefined;
}

/** Convert a stored exam date (date-only or full ISO) to the yyyy-mm-dd a date input expects. */
export function toDateInputValue(iso: string | undefined): string {
  return extractExamDateKey(iso) ?? '';
}
